<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const loading = ref(false);
const error = ref(null);
const data = ref(null);

const now = new Date();
const sinceDate = ref(
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
);
const untilDate = ref(now.toISOString().slice(0, 10));

const MONTH_NAMES = [
  '',
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const CATEGORY_LABELS = {
  marketing: 'Marketing',
  utility: 'Utilidade',
  authentication: 'Autenticação',
  other: 'Outros',
};

const CATEGORY_COLORS = {
  marketing: 'bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300',
  utility: 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300',
  authentication: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300',
  other: 'bg-gray-100 dark:bg-gray-500/15 text-gray-600 dark:text-gray-300',
};

const credits = computed(() => data.value?.credits || null);

const isUnlimited = computed(() => credits.value?.balance === -1);

const balanceReais = computed(() => {
  if (!credits.value || isUnlimited.value) return '∞';
  return (credits.value.balance * credits.value.credit_price)
    .toFixed(2)
    .replace('.', ',');
});

const creditPriceFormatted = computed(() => {
  if (!credits.value) return '0,000';
  return credits.value.credit_price.toFixed(3).replace('.', ',');
});

const costInPeriodFormatted = computed(() => {
  if (!credits.value) return '0,00';
  return credits.value.cost_in_period.toFixed(2).replace('.', ',');
});

const balancePercentage = computed(() => {
  if (!credits.value || isUnlimited.value) return 100;
  if (!credits.value.balance) return 0;
  const total = credits.value.balance + credits.value.used_in_period;
  if (total === 0) return 100;
  return Math.min(100, Math.round((credits.value.balance / total) * 100));
});

const balanceBarColor = computed(() => {
  if (isUnlimited.value) return 'bg-emerald-500';
  const pct = balancePercentage.value;
  if (pct > 50) return 'bg-emerald-500';
  if (pct > 20) return 'bg-amber-500';
  return 'bg-red-500';
});

async function fetchUsage() {
  loading.value = true;
  error.value = null;
  try {
    const res = await InboxesAPI.getTemplateUsage(props.inbox.id, {
      since: sinceDate.value,
      until: untilDate.value,
    });
    data.value = res.data;
  } catch (err) {
    error.value = err?.response?.data?.error || err.message;
  } finally {
    loading.value = false;
  }
}

function exportCSV() {
  if (!data.value) return;

  let csv = 'Template,Categoria,Quantidade,Créditos\n';
  data.value.by_template.forEach(t => {
    const cost = credits.value?.costs?.[t.category] || 1;
    csv += `"${t.name}","${CATEGORY_LABELS[t.category] || t.category}",${t.count},${t.count * cost}\n`;
  });

  if (data.value.by_month.length) {
    csv += '\nMês,Ano,Total,Marketing,Utilidade,Autenticação,Outros\n';
    data.value.by_month.forEach(m => {
      csv += `${MONTH_NAMES[m.month]},${m.year},${m.total},${m.marketing},${m.utility},${m.authentication},${m.other}\n`;
    });
  }

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `template-usage-${sinceDate.value}-${untilDate.value}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

onMounted(fetchUsage);
watch([sinceDate, untilDate], fetchUsage);
</script>

<template>
  <div class="mx-6 max-w-5xl">
    <div class="flex flex-col gap-1 mb-6">
      <h3 class="text-lg font-semibold text-n-slate-12">
        Uso de Templates WhatsApp
      </h3>
      <p class="text-sm text-n-slate-10">
        Relatório de templates enviados pela API Cloud para análise de uso e
        cobrança mensal.
      </p>
    </div>

    <!-- Credits Balance Section -->
    <div
      v-if="credits"
      class="mb-8 p-5 rounded-2xl border-2 border-n-weak bg-gradient-to-br from-n-background to-n-alpha-1"
    >
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-2">
          <div
            class="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-500/15"
          >
            <svg
              class="w-5 h-5 text-emerald-600 dark:text-emerald-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h4 class="text-base font-semibold text-n-slate-12">
            Saldo Pré-Pago
          </h4>
        </div>
        <span class="text-xs text-n-slate-10">
          1 crédito = R$ {{ creditPriceFormatted }}
        </span>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <!-- Balance Card -->
        <div
          class="p-4 rounded-xl border-2 border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 flex flex-col gap-1"
        >
          <span class="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase"
            >Saldo Atual</span
          >
          <div v-if="isUnlimited" class="flex items-baseline gap-2">
            <span class="text-3xl font-bold text-emerald-700 dark:text-emerald-300">∞</span>
            <span class="text-sm text-emerald-600 dark:text-emerald-400">Ilimitado</span>
          </div>
          <template v-else>
            <div class="flex items-baseline gap-2">
              <span class="text-3xl font-bold text-emerald-700 dark:text-emerald-300">{{
                credits.balance.toLocaleString('pt-BR')
              }}</span>
              <span class="text-sm text-emerald-600 dark:text-emerald-400">créditos</span>
            </div>
            <span class="text-xs text-emerald-600 dark:text-emerald-400"
              >≈ R$ {{ balanceReais }}</span
            >
          </template>
        </div>

        <!-- Used in Period Card -->
        <div
          class="p-4 rounded-xl border border-n-weak bg-n-solid-2 flex flex-col gap-1"
        >
          <span class="text-xs font-medium text-n-slate-10 uppercase"
            >Consumido no Período</span
          >
          <div class="flex items-baseline gap-2">
            <span class="text-3xl font-bold text-n-slate-12">{{
              credits.used_in_period.toLocaleString('pt-BR')
            }}</span>
            <span class="text-sm text-n-slate-10">créditos</span>
          </div>
          <span class="text-xs text-n-slate-10"
            >≈ R$ {{ costInPeriodFormatted }}</span
          >
        </div>

        <!-- Costs Card -->
        <div
          class="p-4 rounded-xl border border-n-weak bg-n-solid-2 flex flex-col gap-2"
        >
          <span class="text-xs font-medium text-n-slate-10 uppercase"
            >Custo por Categoria</span
          >
          <div class="flex flex-col gap-1.5 text-sm">
            <div class="flex justify-between">
              <span class="text-blue-700 dark:text-blue-400">●&nbsp; Utilidade</span>
              <span class="font-semibold text-n-slate-12"
                >{{ credits.costs.utility }} créd.</span
              >
            </div>
            <div class="flex justify-between">
              <span class="text-purple-700 dark:text-purple-400">●&nbsp; Marketing</span>
              <span class="font-semibold text-n-slate-12"
                >{{ credits.costs.marketing }} créd.</span
              >
            </div>
            <div class="flex justify-between">
              <span class="text-amber-700 dark:text-amber-400">●&nbsp; Autenticação</span>
              <span class="font-semibold text-n-slate-12"
                >{{ credits.costs.authentication }} créd.</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Balance Bar -->
      <div v-if="!isUnlimited" class="w-full h-2.5 bg-n-alpha-2 rounded-full overflow-hidden">
        <div
          class="h-full rounded-full transition-all duration-500"
          :class="balanceBarColor"
          :style="{ width: balancePercentage + '%' }"
        />
      </div>
      <div class="flex justify-between mt-1">
        <span v-if="isUnlimited" class="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
          ✓ Plano ilimitado — sem restrição de envios
        </span>
        <template v-else>
          <span class="text-xs text-n-slate-10">
            {{ balancePercentage }}% restante
          </span>
          <span
            v-if="credits.balance <= 0"
            class="text-xs font-semibold text-red-600 dark:text-red-400"
          >
            ⚠ Saldo esgotado — envios de templates bloqueados
          </span>
          <span
            v-else-if="credits.balance <= 50"
            class="text-xs font-semibold text-amber-600 dark:text-amber-400"
          >
            ⚠ Saldo baixo — considere adicionar créditos
          </span>
        </template>
      </div>
    </div>

    <div class="flex flex-wrap items-end gap-4 mb-6">
      <div>
        <label class="text-xs font-medium text-n-slate-11 block mb-1"
          >De</label
        >
        <input
          v-model="sinceDate"
          type="date"
          class="px-3 py-2 border border-n-weak rounded-lg text-sm bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/50"
        />
      </div>
      <div>
        <label class="text-xs font-medium text-n-slate-11 block mb-1"
          >Até</label
        >
        <input
          v-model="untilDate"
          type="date"
          class="px-3 py-2 border border-n-weak rounded-lg text-sm bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/50"
        />
      </div>
      <button
        class="px-4 py-2 text-sm font-medium text-n-blue-9 border border-n-blue-7 rounded-lg hover:bg-n-blue-2 transition-colors"
        @click="fetchUsage"
      >
        Atualizar
      </button>
      <button
        v-if="data && data.summary.total > 0"
        class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:bg-n-brand-hover transition-colors"
        @click="exportCSV"
      >
        Exportar CSV
      </button>
    </div>

    <div
      v-if="loading"
      class="flex items-center justify-center py-16 text-n-slate-10"
    >
      <svg
        class="animate-spin h-6 w-6 mr-2"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      Carregando...
    </div>

    <div
      v-else-if="error"
      class="p-4 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 text-sm"
    >
      Erro ao carregar dados: {{ error }}
    </div>

    <template v-else-if="data">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div
          class="p-4 rounded-xl border border-n-weak bg-n-solid-2 flex flex-col gap-1"
        >
          <span class="text-xs font-medium text-n-slate-10 uppercase"
            >Total</span
          >
          <span class="text-2xl font-bold text-n-slate-12">{{
            data.summary.total
          }}</span>
        </div>
        <div
          class="p-4 rounded-xl border border-purple-200 dark:border-purple-500/30 bg-purple-50 dark:bg-purple-500/10 flex flex-col gap-1"
        >
          <span class="text-xs font-medium text-purple-600 dark:text-purple-400 uppercase"
            >Marketing</span
          >
          <span class="text-2xl font-bold text-purple-700 dark:text-purple-300">{{
            data.summary.marketing
          }}</span>
          <span
            v-if="credits"
            class="text-xs text-purple-500 dark:text-purple-400"
            >{{ data.summary.marketing * credits.costs.marketing }} créd.</span
          >
        </div>
        <div
          class="p-4 rounded-xl border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 flex flex-col gap-1"
        >
          <span class="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase"
            >Utilidade</span
          >
          <span class="text-2xl font-bold text-blue-700 dark:text-blue-300">{{
            data.summary.utility
          }}</span>
          <span
            v-if="credits"
            class="text-xs text-blue-500 dark:text-blue-400"
            >{{ data.summary.utility * credits.costs.utility }} créd.</span
          >
        </div>
        <div
          class="p-4 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 flex flex-col gap-1"
        >
          <span class="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase"
            >Autenticação</span
          >
          <span class="text-2xl font-bold text-amber-700 dark:text-amber-300">{{
            data.summary.authentication
          }}</span>
          <span
            v-if="credits"
            class="text-xs text-amber-500 dark:text-amber-400"
            >{{ data.summary.authentication * credits.costs.authentication }} créd.</span
          >
        </div>
      </div>

      <div
        v-if="data.summary.total === 0"
        class="text-center py-12 text-n-slate-10"
      >
        <p class="text-base">Nenhum template enviado neste período.</p>
        <p class="text-sm mt-1">
          Ajuste as datas acima para ver outros períodos.
        </p>
      </div>

      <template v-else>
        <div v-if="data.by_month.length" class="mb-8">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-3">
            Uso Mensal
          </h4>
          <div class="overflow-x-auto rounded-xl border border-n-weak">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-n-alpha-1 text-n-slate-11">
                  <th class="text-left px-4 py-3 font-medium">Mês</th>
                  <th class="text-right px-4 py-3 font-medium">Total</th>
                  <th class="text-right px-4 py-3 font-medium">Marketing</th>
                  <th class="text-right px-4 py-3 font-medium">Utilidade</th>
                  <th class="text-right px-4 py-3 font-medium">
                    Autenticação
                  </th>
                  <th class="text-right px-4 py-3 font-medium">Outros</th>
                  <th
                    v-if="credits"
                    class="text-right px-4 py-3 font-medium text-emerald-700 dark:text-emerald-400"
                  >
                    Créditos
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in data.by_month"
                  :key="`${row.year}-${row.month}`"
                  class="border-t border-n-weak hover:bg-n-alpha-1 transition-colors"
                >
                  <td class="px-4 py-3 text-n-slate-12">
                    {{ MONTH_NAMES[row.month] }} {{ row.year }}
                  </td>
                  <td
                    class="px-4 py-3 text-right font-semibold text-n-slate-12"
                  >
                    {{ row.total }}
                  </td>
                  <td class="px-4 py-3 text-right text-purple-700 dark:text-purple-400">
                    {{ row.marketing }}
                  </td>
                  <td class="px-4 py-3 text-right text-blue-700 dark:text-blue-400">
                    {{ row.utility }}
                  </td>
                  <td class="px-4 py-3 text-right text-amber-700 dark:text-amber-400">
                    {{ row.authentication }}
                  </td>
                  <td class="px-4 py-3 text-right text-n-slate-10">
                    {{ row.other }}
                  </td>
                  <td
                    v-if="credits"
                    class="px-4 py-3 text-right font-semibold text-emerald-700 dark:text-emerald-400"
                  >
                    {{
                      (row.marketing * credits.costs.marketing) +
                      (row.utility * credits.costs.utility) +
                      (row.authentication * credits.costs.authentication) +
                      (row.other * credits.costs.other)
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="data.by_template.length">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-3">
            Detalhamento por Template
          </h4>
          <div class="overflow-x-auto rounded-xl border border-n-weak">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-n-alpha-1 text-n-slate-11">
                  <th class="text-left px-4 py-3 font-medium">Template</th>
                  <th class="text-left px-4 py-3 font-medium">Categoria</th>
                  <th class="text-right px-4 py-3 font-medium">Envios</th>
                  <th
                    v-if="credits"
                    class="text-right px-4 py-3 font-medium text-emerald-700 dark:text-emerald-400"
                  >
                    Créditos
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="tpl in data.by_template"
                  :key="tpl.name"
                  class="border-t border-n-weak hover:bg-n-alpha-1 transition-colors"
                >
                  <td class="px-4 py-3 text-n-slate-12 font-mono text-xs">
                    {{ tpl.name }}
                  </td>
                  <td class="px-4 py-3">
                    <span
                      class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                      :class="
                        CATEGORY_COLORS[tpl.category] || CATEGORY_COLORS.other
                      "
                    >
                      {{ CATEGORY_LABELS[tpl.category] || tpl.category }}
                    </span>
                  </td>
                  <td
                    class="px-4 py-3 text-right font-semibold text-n-slate-12"
                  >
                    {{ tpl.count }}
                  </td>
                  <td
                    v-if="credits"
                    class="px-4 py-3 text-right font-semibold text-emerald-700 dark:text-emerald-400"
                  >
                    {{ tpl.count * (credits.costs[tpl.category] || 1) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>
